import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL, FREEMIUM_LIMITS, LOGO_URL } from './lib/config';
import { ToastContainer, ToastProvider, useToast } from './context/ToastContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useResume } from './hooks/useResume';
import { useEmailDrafts } from './hooks/useEmailDrafts';
import { useSearchAndChat } from './hooks/useSearchAndChat';
import { useConnectCRM } from './hooks/useConnectCRM';
import { useLegalContent } from './hooks/useLegalContent';
import { useSettingsContent } from './hooks/useSettingsContent';
import { useAppBootstrap } from './hooks/useAppBootstrap';
import { LadderHeader } from './components/LadderHeader';
import { ChatSidebar } from './components/ChatSidebar';
import { SearchSection } from './components/SearchSection';
import { SearchMessages } from './components/SearchMessages';
import { ResultsHeader } from './components/ResultsHeader';
import { ResultsGrid } from './components/ResultsGrid';
import { SavedAlumniTable } from './components/SavedAlumniTable';
import { EmailAuthModal } from './components/EmailAuthModal';
import { LegalModal } from './components/LegalModal';
import { UpgradeModal } from './components/UpgradeModal';
import { SettingsModal } from './components/SettingsModal';
import { EmailDraftModal } from './components/EmailDraftModal';
import { CreateCategoryModal } from './components/CreateCategoryModal';
import { NotesModal } from './components/NotesModal';
import { MoreInfoModal } from './components/MoreInfoModal';
import Login from './Login';

console.log('🔧 Using API Base URL:', API_BASE_URL);
console.log('🌍 Environment:', process.env.NODE_ENV);
console.log('🔧 Custom API URL:', process.env.REACT_APP_API_URL || 'Not set');

const SEARCH_SUGGESTIONS = [
  'Product Managers at Meta',
  'Data Scientists in San Francisco',
  'Investment Banking Associates',
  'Econ Major in Bain',
];
const VIEW_MODE_STORAGE_KEY = 'ladder_view_mode';

const UNIVERSITY_OPTIONS = [
  { id: 'ucsb', name: 'UCSB' },
  { id: 'ucb', name: 'UC Berkeley' },
  { id: 'ucla', name: 'UCLA' },
  { id: 'uci', name: 'UC Irvine' },
  { id: 'santa_clara', name: 'Santa Clara' },
];

function LadderApp() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toasts, showToast, removeToast } = useToast();
  const [currentChatIdForConnect, setCurrentChatIdForConnect] = useState(null);
  const {
    isSignedIn,
    userId,
    fullName,
    userBio,
    setUserBio,
    userUsageStats,
    showEmailAuth,
    showUpgradeModal,
    upgradeReason,
    handleGoogleAuth,
    checkFreemiumLimit,
    closeUpgradeModal,
    incrementUsage,
    ensureUserRecordExists,
    authLoading,
  } = useAuth();
  const {
    showLegalModal,
    legalTitle,
    legalContentType,
    privacyContent,
    termsContent,
    openLegalModal,
    closeLegalModal,
  } = useLegalContent();
  const { settingsPage, showSettingsPopup, openSettings, closeSettings, handleSettingsNavigate } = useSettingsContent();
  const {
    savedAlumniRows,
    userCategories,
    categories,
    comments,
    expandedCells,
    selectedCategoryFilter,
    setSelectedCategoryFilter,
    showMoreInfoModal,
    selectedAlumniForInfo,
    openMoreInfoModal,
    closeMoreInfoModal,
    manualEntry,
    addingManual,
    showManualEntry,
    toggleManualEntry,
    handleManualEntry,
    handleManualEntryChange,
    showCreateCategory,
    setShowCreateCategory,
    newCategoryName,
    setNewCategoryName,
    showCategoryDropdown,
    toggleCategoryDropdown,
    toggleCellExpansion,
    sortColumn,
    sortDirection,
    filters,
    setFilters,
    openFilterDropdown,
    setOpenFilterDropdown,
    pendingFilters,
    setPendingFilters,
    handleSort,
    clearAllFilters,
    getUniqueValuesWithCounts,
    getFilteredRowsExcluding,
    showNotesModal,
    tempNotes,
    handleNotesChange,
    handleFieldUpdate,
    openNotesModal,
    closeNotesModal,
    createCustomCategory,
    updateAlumniCategory,
    deleteCategory,
    handleRemoveAlumni,
    refreshSavedAlumni,
    saveAlumni,
    clearSavedAlumniData,
    sortedTableRows,
    totalCount,
    filteredCount,
    activeFilterCount,
    // REMOVED: updateChatEmailFilter - no longer needed, filtering by chat_id in database
  } = useConnectCRM({ userId, showToast, ensureUserRecordExists, currentChatId: currentChatIdForConnect });

  // Debug logging for userId and saved alumni
  useEffect(() => {
    console.log('🔍 LadderApp - Current userId:', userId);
    console.log('🔍 LadderApp - Saved alumni count:', savedAlumniRows.length);
    if (savedAlumniRows.length > 0) {
      console.log('🔍 LadderApp - Sample saved alumni:', savedAlumniRows.slice(0, 3));
    }
  }, [savedAlumniRows, userId]);
  const {
    query,
    setQuery,
    searchLoading,
    loadMoreLoading,
    results,
    messagesEndRef,
    hasMoreCandidates,
    viewMode,
    setViewMode,
    lastSearchQuery,
    selectedUniversities,
    chats,
    currentChatId,
    showChatSidebar,
    setShowChatSidebar,
    loadingChats,
    creatingNewChat,
    setCreatingNewChat,
    isCreatingChat,
    setIsCreatingChat,
    newChatName,
    setNewChatName,
    editingChatId,
    setEditingChatId,
    editingChatName,
    setEditingChatName,
    candidateEmails,
    candidateSaved,
    emailLoading,
    handleSubmit,
    handleLoadMoreCandidates,
    handleGetEmail,
    loadChatHistory,
    createNewChat,
    switchToChat,
    updateChatName,
    deleteChat,
    resetSearchState,
    handleQuickSearch,
    handleToggleUniversity,
    resultCount,
    savedCount,
    hasResultSummary,
  } = useSearchAndChat({
    userId,
    showToast,
    savedAlumniRows,
    checkFreemiumLimit,
    ensureUserRecordExists,
    incrementUsage,
    saveAlumni,
    refreshSavedAlumni,
  });
  const { uploadingResume, handleFileInputChange } = useResume({ userId, showToast, setUserBio });
  const {
    drafts,
    showEmailModal,
    selectedAlumniForEmail,
    loadingDraftId,
    sendingDraftId,
    refiningDraftId,
    showRefineInput,
    refineInstructions,
    openEmailModal,
    closeEmailModal,
    setShowRefineInput,
    setRefineInstructions,
    handleSubjectChange,
    handleBodyChange,
    handleSubjectBlur,
    handleBodyBlur,
    generateDraft,
    refineEmail,
    sendEmail,
    resetEmailDrafts,
  } = useEmailDrafts({
    userId,
    fullName,
    userBio,
    showToast,
    checkFreemiumLimit,
    incrementUsage,
    ensureUserRecordExists,
  });
  const { testBackend, forceSignOut } = useAppBootstrap({
    clearSavedAlumniData,
    resetSearchState,
    resetEmailDrafts,
  });
  const isConnectRoute = location.pathname.startsWith('/connect');
  const handleViewModeChange = useCallback(
    mode => {
      setViewMode(mode);
      try {
        localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
      } catch (error) {
        console.warn('Unable to persist view mode:', error);
      }
      if (mode === 'table') {
        if (!isConnectRoute) {
          navigate('/connect');
        }
      } else if (mode === 'cards') {
        if (isConnectRoute) {
          navigate('/');
        }
      }
    },
    [isConnectRoute, navigate, setViewMode],
  );

  // REMOVED: useEffect for updateChatEmailFilter - saved alumni now load once per user login

  const handleLogout = () => {
    closeSettings();
    forceSignOut();
  };

  const handleOpenNotesModal = row => {
    openNotesModal(row.id, comments[row.id] || '');
  };
  const toggleRefineInput = alumniId => {
    setShowRefineInput(prev => ({ ...prev, [alumniId]: !prev[alumniId] }));
  };
  const handleRefineInstructionsChange = (alumniId, value) => {
    setRefineInstructions(prev => ({ ...prev, [alumniId]: value }));
  };
  const handleUpgradeClick = () => {
    showToast('Upgrade flow coming soon!', 'info');
    closeUpgradeModal();
  };

  // Auto-categorize all alumni without categories
  // Load chat history when user signs in
  // Also refresh saved alumni when userId becomes available
  useEffect(() => {
    if (userId) {
      console.log('🔄 userId available, initializing app data for userId:', userId);
      testBackend();
      loadChatHistory();
      refreshSavedAlumni(null, true);
    }
  }, [loadChatHistory, refreshSavedAlumni, testBackend, userId]);

  useEffect(() => {
    setCurrentChatIdForConnect(currentChatId);
  }, [currentChatId]);

  useEffect(() => {
    const nextMode = isConnectRoute ? 'table' : 'cards';
    setViewMode(nextMode);
    try {
      localStorage.setItem(VIEW_MODE_STORAGE_KEY, nextMode);
    } catch (error) {
      console.warn('Unable to persist view mode on route change:', error);
    }
  }, [isConnectRoute, setViewMode]);

  const shouldShowConnectTable = viewMode === 'table';

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="flex flex-col h-screen bg-[#1a1d24] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#374151] border-t-[#e4e6eb] rounded-full animate-spin"></div>
          <div className="text-[#e4e6eb] text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  // Show login page if user is not signed in
  if (!isSignedIn) {
    return <Login onGoogleAuth={handleGoogleAuth} />;
  }

  return (
    <div className="flex flex-col h-screen bg-[#1a1d24] text-[#e4e6eb]">
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <LadderHeader
        isSignedIn={isSignedIn}
        viewMode={viewMode}
        onChangeViewMode={handleViewModeChange}
        onToggleChatSidebar={() => setShowChatSidebar(prev => !prev)}
        onAuthClick={handleGoogleAuth}
        onLogoutClick={handleLogout}
        onOpenSettings={() => openSettings('overview')}
      />

      <EmailAuthModal isOpen={showEmailAuth} onGoogleAuth={handleGoogleAuth} onOpenLegal={openLegalModal} />

      <ChatSidebar
        isOpen={isSignedIn && showChatSidebar}
        onClose={() => setShowChatSidebar(false)}
        chats={chats}
        currentChatId={currentChatId}
        loadingChats={loadingChats}
        creatingNewChat={creatingNewChat}
        isCreatingChat={isCreatingChat}
        onStartCreateChat={() => setCreatingNewChat(true)}
        newChatName={newChatName}
        onNewChatNameChange={setNewChatName}
        onCreateNewChat={() => newChatName.trim() && createNewChat(newChatName)}
        onCancelCreateNewChat={() => {
          setCreatingNewChat(false);
          setNewChatName('');
          setIsCreatingChat(false);
        }}
        onSwitchChat={switchToChat}
        editingChatId={editingChatId}
        editingChatName={editingChatName}
        onChangeEditingChatName={setEditingChatName}
        onStartEditingChat={chat => {
          setEditingChatId(chat.id);
          setEditingChatName(chat.name || '');
        }}
        onCommitEditingChat={() => {
          if (editingChatId) {
            updateChatName(editingChatId, editingChatName || '');
          }
        }}
        onCancelEditingChat={() => {
          setEditingChatId(null);
          setEditingChatName('');
        }}
        onDeleteChat={deleteChat}
      />
      <LegalModal
        isOpen={showLegalModal}
        title={legalTitle}
        contentType={legalContentType}
        privacyContent={privacyContent}
        termsContent={termsContent}
        onClose={closeLegalModal}
      />

      <UpgradeModal isOpen={showUpgradeModal} reason={upgradeReason} logoUrl={LOGO_URL} onUpgrade={handleUpgradeClick} onClose={closeUpgradeModal} />

      <SettingsModal
        isOpen={showSettingsPopup}
        page={settingsPage || 'overview'}
        onClose={closeSettings}
        onNavigate={handleSettingsNavigate}
        usageStats={userUsageStats}
        freemiumLimits={FREEMIUM_LIMITS}
        privacyContent={privacyContent}
        termsContent={termsContent}
      />

      {/* Main content area */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex flex-col h-full">

        {/* Chat Area */}

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto py-6 px-6">
                <SearchSection
                  isSignedIn={isSignedIn}
                  query={query}
                  lastSearchQuery={lastSearchQuery}
                  suggestions={SEARCH_SUGGESTIONS}
                  universities={UNIVERSITY_OPTIONS}
                  selectedUniversities={selectedUniversities}
                  onQueryChange={setQuery}
                  onSubmit={handleSubmit}
                  onQuickSearch={handleQuickSearch}
                  onToggleUniversity={handleToggleUniversity}
                />

                <SearchMessages results={results} />

                {isSignedIn && hasResultSummary && <ResultsHeader resultCount={resultCount} savedCount={savedCount} />}

                <ResultsGrid
                  isSignedIn={isSignedIn}
                  viewMode={viewMode}
                  results={results}
                  candidateSaved={candidateSaved}
                  candidateEmails={candidateEmails}
                  emailLoading={emailLoading}
                  onGetEmail={handleGetEmail}
                  onMoreInfo={openMoreInfoModal}
                  hasMoreCandidates={hasMoreCandidates}
                  onLoadMore={handleLoadMoreCandidates}
                  loadMoreLoading={loadMoreLoading}
                  searchLoading={searchLoading}
                />
                
                {/* Table View */}
                    {shouldShowConnectTable && (
                  <>
                    <SavedAlumniTable
                      rows={sortedTableRows}
                      totalCount={totalCount}
                      filteredCount={filteredCount}
                      activeFilterCount={activeFilterCount}
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onClearFilters={clearAllFilters}
                      onSort={handleSort}
                      filters={filters}
                      onUpdateFilters={setFilters}
                      pendingFilters={pendingFilters}
                      openFilterDropdown={openFilterDropdown}
                      setOpenFilterDropdown={setOpenFilterDropdown}
                      setPendingFilters={setPendingFilters}
                      getUniqueValuesWithCounts={getUniqueValuesWithCounts}
                      getFilteredRowsExcluding={getFilteredRowsExcluding}
                      categories={categories}
                      showCategoryDropdown={showCategoryDropdown}
                      onToggleCategoryDropdown={toggleCategoryDropdown}
                      selectedCategoryFilter={selectedCategoryFilter}
                      onSelectCategoryFilter={setSelectedCategoryFilter}
                      userCategories={userCategories}
                      showManualEntry={showManualEntry}
                      manualEntry={manualEntry}
                      onManualEntryChange={handleManualEntryChange}
                      onManualEntrySubmit={handleManualEntry}
                      addingManual={addingManual}
                      onToggleManualEntry={toggleManualEntry}
                      candidateSaved={candidateSaved}
                      candidateEmails={candidateEmails}
                      onOpenEmailModal={openEmailModal}
                      onOpenNotesModal={handleOpenNotesModal}
                      onRemoveAlumni={handleRemoveAlumni}
                      onFieldUpdate={handleFieldUpdate}
                      comments={comments}
                      expandedCells={expandedCells}
                      onToggleCellExpansion={toggleCellExpansion}
                      updateAlumniCategory={updateAlumniCategory}
                      deleteCategory={deleteCategory}
                      onOpenCreateCategory={() => setShowCreateCategory(true)}
                      showCreateCategory={false}
                      onFileInputChange={handleFileInputChange}
                      uploadingResume={uploadingResume}
                    />
                    {sortedTableRows.length === 0 && (
                      <div className="mt-6 p-6 bg-[#23242a] rounded-xl text-gray-300">
                        <p className="font-semibold text-white mb-2">No saved alumni yet</p>
                        <p className="text-sm">
                          Save alumni from the search results to build your Connect CRM list. Once saved, they will appear here for easy follow-up.
                        </p>
                      </div>
                    )}
                  </>
                )}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>
      </div>
      <EmailDraftModal
          isOpen={showEmailModal}
          alumni={selectedAlumniForEmail}
          drafts={drafts}
          loadingDraftId={loadingDraftId}
          refiningDraftId={refiningDraftId}
          sendingDraftId={sendingDraftId}
          showRefineInput={showRefineInput}
          refineInstructions={refineInstructions}
          onClose={closeEmailModal}
          onGenerate={generateDraft}
          onRefine={refineEmail}
          onSend={sendEmail}
          onToggleRefineInput={toggleRefineInput}
          onRefineInstructionsChange={handleRefineInstructionsChange}
          onSubjectChange={handleSubjectChange}
          onBodyChange={handleBodyChange}
          onSubjectBlur={handleSubjectBlur}
          onBodyBlur={handleBodyBlur}
        />

        <CreateCategoryModal
          isOpen={showCreateCategory}
          categoryName={newCategoryName}
          onChangeName={setNewCategoryName}
          onCreate={createCustomCategory}
          onCancel={() => {
            setShowCreateCategory(false);
            setNewCategoryName('');
          }}
        />

        <NotesModal isOpen={showNotesModal} notes={tempNotes} onChange={handleNotesChange} onClose={closeNotesModal} />
        <MoreInfoModal isOpen={showMoreInfoModal} alumni={selectedAlumniForInfo} onClose={closeMoreInfoModal} />

    </div>
  );
}

function LadderFrontend() {
  return (
    <AuthProvider>
      <ToastProvider>
        <LadderApp />
      </ToastProvider>
    </AuthProvider>
  );
}

export default LadderFrontend;
